import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, Switch } from '@vnetwork/ui/primitives';
import type { Specification } from '@/api/capacities';

export interface ModelSpecificationsEditorProps {
  specs: Specification[];
  onChange: (specs: Specification[]) => void;
}

const EMPTY_ROW: Specification = { name: '', value: '', unit: '', is_primary: false };

/**
 * Dynamic Specifications sub-editor for the Model form — one row per spec
 * (name · value · unit · primary), with add/remove. Emits the full array up on
 * every change; the parent owns state.
 */
export function ModelSpecificationsEditor({ specs, onChange }: ModelSpecificationsEditorProps) {
  const update = (i: number, patch: Partial<Specification>) =>
    onChange(specs.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => onChange(specs.filter((_, idx) => idx !== i));
  const add = () => onChange([...specs, { ...EMPTY_ROW }]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {specs.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          No specifications yet.
        </p>
      ) : (
        specs.map((spec, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1.4fr 0.8fr auto auto',
              gap: 'var(--vn-space-2)',
              alignItems: 'center',
            }}
          >
            <Input
              placeholder="Name"
              aria-label={`Specification ${i + 1} name`}
              value={spec.name}
              onChange={(e) => update(i, { name: e.target.value })}
            />
            <Input
              placeholder="Value"
              aria-label={`Specification ${i + 1} value`}
              value={spec.value}
              onChange={(e) => update(i, { value: e.target.value })}
            />
            <Input
              placeholder="Unit"
              aria-label={`Specification ${i + 1} unit`}
              value={spec.unit ?? ''}
              onChange={(e) => update(i, { unit: e.target.value })}
            />
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--vn-space-1)',
                fontSize: 'var(--vn-font-size-sm)',
                color: 'var(--vn-fg-muted)',
              }}
            >
              <Switch
                checked={!!spec.is_primary}
                onCheckedChange={(v) => update(i, { is_primary: v })}
                aria-label={`Specification ${i + 1} primary`}
              />
              Primary
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(i)}
              aria-label={`Remove specification ${i + 1}`}
            >
              <Trash2 size={16} />
            </Button>
          </div>
        ))
      )}
      <div>
        <Button type="button" variant="ghost" size="sm" onClick={add} startIcon={<Plus size={16} />}>
          Add specification
        </Button>
      </div>
    </div>
  );
}
